/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {FormattedMessage, FormattedNumber, useIntl} from 'react-intl'
import {Tooltip} from '@chakra-ui/react'
import {
    Box,
    Flex,
    Stack,
    Text,
    IconButton,
    AccordionIcon,
    useBreakpointValue,
    useDisclosure,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {PromoCode} from '../promo-code'
import LoyaltyCoupon from '../loyalty-coupon'
import PromoCodeDisplay from '../../pages/checkout/partials/promo-code'
import CartItemsAccordion from './partials/cart-items-accordion'
import FocusedAccordion from '../focused-accordion'
import WebDrawer from '../web-drawer'
import ProductItemList from '../product-item-list'
import Link from '@salesforce/retail-react-app/app/components/link'
import SavingsAccordion from './partials/savings-accordion'
import {InfoCircle} from '../../components/icons'
import {useBrand} from '../../contexts/brand-context'
import {useCheckoutProgress} from '../../contexts/checkout-progress-context'
import GlobalErrorCallout from '../global-error-callout'
import useHideLoyaltyUIForInternational from '@app/hooks/use-hide-loyalty-ui-for-international'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

const renderOrderSummaryContent = ({
    showHeading,
    isCheckout,
    isEstimate,
    isOrderConfirmation,
    basket,
    orderSummaryTheme,
    shippingItem,
    showPoints,
    showCartItems,
    totalItems,
    productsDiscountTotal,
    footerContent,
    shouldHideLoyaltyUI,
    buildUrl,
    pointsEarned,
    ...otherProps
}) => {
    const {isGarage} = useBrand()
    const giftCardTotal =
        otherProps?.page === 'cart'
            ? 0
            : (basket.paymentInstruments || [])
                  .filter((instrument) => instrument.paymentMethodId === 'SMARTCLIXX')
                  .reduce((sum, instrument) => sum + (instrument.amount || 0), 0)

    const loyaltyCoupon = basket?.couponItems?.filter(
        (ci) => ci?.couponItemId === basket?.c_sflmLoyaltyCouponID
    )
    const loyaltyCouponCode = loyaltyCoupon && loyaltyCoupon.length ? loyaltyCoupon[0].code : ''
    const discountedMerchandiseTotal = Number(basket?.productTotal ?? basket?.productSubTotal) || 0
    const totalSavingsAmount = Math.abs(Number(basket?.c_savings?.totalOrderDiscount) || 0)
    const subtotalValue = discountedMerchandiseTotal + totalSavingsAmount
    const estimatedShipping = Number(basket?.shippingTotal) || 0
    const estimatedTax = basket?.taxTotal != null ? Number(basket.taxTotal) : 0
    const estimatedTotalValue =
        basket?.orderTotal != null
            ? Number(basket.orderTotal)
            : (Number(basket?.productTotal) || discountedMerchandiseTotal) + estimatedShipping + estimatedTax
    const hasPointsEarned = Number(pointsEarned) > 0
    const shouldShowPointsPreview =
        showPoints &&
        !shouldHideLoyaltyUI &&
        hasPointsEarned &&
        !isOrderConfirmation &&
        otherProps?.page !== 'cart'

    const shouldShowPointsBanner =
        showPoints &&
        !shouldHideLoyaltyUI &&
        hasPointsEarned &&
        (basket?.exportStatus === 'ready' || isOrderConfirmation)
    const shouldShowLoyaltyPreview =
        !shouldHideLoyaltyUI && hasPointsEarned && !isOrderConfirmation && otherProps?.page !== 'cart'
    const shouldShowPointsSection = shouldShowPointsBanner || shouldShowPointsPreview

    return (
        <>
            {shouldShowPointsSection && (
                <Stack {...orderSummaryTheme.pointsStack}>
                    {/* TODO: update the condition (whether points preview should be displayed) and points can be earned */}
                    {shouldShowPointsBanner &&
                        (isOrderConfirmation ? (
                            <LoyaltyCoupon
                                mode="preview"
                                pointsEarned={basket?.c_loyaltyV2PointsEarned}
                                previewContent={
                                    isGarage ? (
                                        <FormattedMessage
                                            id="checkout_confirmation.points_earned_when_shipped.garage"
                                            defaultMessage="You'll earn <b>{points}</b> points once your order is shipped!"
                                            values={{
                                                points: (
                                                    <Text as="span" data-private>
                                                        {pointsEarned}
                                                    </Text>
                                                ),
                                                b: (chunks) => (
                                                    <Text as="span" textStyle="bodyXsBold">
                                                        {chunks}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            id="checkout_confirmation.points_earned_when_shipped.dynamite"
                                            defaultMessage="You'll earn <b>{points}</b> points once your order is shipped!"
                                            values={{
                                                points: (
                                                    <Text as="span" data-private>
                                                        {pointsEarned}
                                                    </Text>
                                                ),
                                                b: (chunks) => (
                                                    <Text as="span" textStyle="bodyXsBold">
                                                        {chunks}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    )
                                }
                                previewActionContent={
                                    <FormattedMessage
                                        id="checkout_confirmation.button.see_rewards"
                                        defaultMessage="See my rewards"
                                    />
                                }
                                previewActionHref={buildUrl('/my-rewards')}
                            />
                        ) : (
                            <Flex {...orderSummaryTheme.pointsBanner}>
                                <Text {...orderSummaryTheme.pointsBannerText}>
                                    {isGarage ? (
                                        <FormattedMessage
                                            defaultMessage="It's so close! You'll earn {points} points once your order is shipped!"
                                            id="checkout_confirmation.points_earned_registered.garage"
                                            values={{
                                                points: (
                                                    <Text as="span" data-private>
                                                        {pointsEarned}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="It's so close! You'll earn {points} points once your order is shipped!"
                                            id="checkout_confirmation.points_earned_registered.dynamite"
                                            values={{
                                                points: (
                                                    <Text as="span" data-private>
                                                        {pointsEarned}
                                                    </Text>
                                                )
                                            }}
                                        />
                                    )}
                                </Text>
                            </Flex>
                        ))}
                    {shouldShowLoyaltyPreview && (
                        <LoyaltyCoupon
                            mode="preview"
                            pointsEarned={basket?.c_loyaltyV2PointsEarned}
                        />
                    )}
                </Stack>
            )}

            {!isCheckout && (
                <Box {...orderSummaryTheme.promoCodeWrapper}>
                    <PromoCode basket={basket} />
                </Box>
            )}

            <Stack {...orderSummaryTheme.summaryStack}>
                {showHeading && (
                    <Text as="h4" {...orderSummaryTheme.heading} id="order-summary-heading">
                        <FormattedMessage
                            defaultMessage="Order Summary"
                            id="order_summary.heading.order_summary"
                        />
                    </Text>
                )}

                {isCheckout && (
                    <PromoCodeDisplay
                        priceAdjustments={basket?.orderPriceAdjustments}
                        loyaltyCouponCode={loyaltyCouponCode}
                    />
                )}

                <Stack {...orderSummaryTheme.contentStack} aria-labelledby="order-summary-heading">
                    <Stack {...orderSummaryTheme.detailsStack}>
                        <Flex {...orderSummaryTheme.row} aria-live="polite" aria-atomic="true">
                            <Text {...orderSummaryTheme.rowText}>
                                <FormattedMessage
                                    defaultMessage="Subtotal"
                                    id="account_order_detail.label.subtotal_store"
                                />
                            </Text>
                            <Text
                                data-testid="order-summary-subtotal"
                                {...orderSummaryTheme.rowText}
                            >
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={subtotalValue}
                                />
                            </Text>
                        </Flex>

                        <SavingsAccordion
                            basket={basket}
                            productsDiscountTotal={productsDiscountTotal}
                        />

                        {giftCardTotal > 0 && (
                            <Flex {...orderSummaryTheme.row} aria-live="polite" aria-atomic="true">
                                <Text {...orderSummaryTheme.rowText}>
                                    <FormattedMessage
                                        defaultMessage="Gift Card"
                                        id="order_summary.label.gift_card"
                                    />
                                </Text>

                                <Text {...orderSummaryTheme.rowText}>
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket?.currency}
                                        value={-giftCardTotal}
                                    />
                                </Text>
                            </Flex>
                        )}

                        <Flex {...orderSummaryTheme.row} aria-live="polite" aria-atomic="true">
                            <Flex {...orderSummaryTheme.shippingLabelWrap}>
                                <Text {...orderSummaryTheme.shippingLabel}>
                                    {isEstimate ? (
                                        <FormattedMessage
                                            defaultMessage="Estimated Shipping"
                                            id="order_summary.label.estimated_shipping"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Shipping"
                                            id="order_summary.label.shipping"
                                        />
                                    )}
                                </Text>

                                {!isCheckout && (
                                    <Tooltip
                                        hasArrow={false}
                                        placement="top"
                                        variant="info"
                                        label={
                                            <FormattedMessage
                                                defaultMessage="Shipping is estimated. Final shipping costs will be calculated at checkout."
                                                id="order_summary.tooltip.estimated_shipping"
                                            />
                                        }
                                    >
                                        <IconButton
                                            variant="unstyled"
                                            display={'flex'}
                                            minW={'auto'}
                                            h={'20px'}
                                            width={'20px'}
                                            aria-label="Aria label button"
                                            icon={<InfoCircle width={'20px'} height={'20px'} />}
                                        />
                                    </Tooltip>
                                )}
                            </Flex>

                            {shippingItem?.priceAdjustments?.some(
                                ({appliedDiscount}) => appliedDiscount?.type === 'free'
                            ) ||
                            (!isCheckout && Number(basket?.shippingTotal) === 0) ? (
                                <Text
                                    as="span"
                                    data-testid="order-summary-shipping"
                                    {...orderSummaryTheme.rowText}
                                >
                                    {isGarage ? (
                                        <FormattedMessage
                                            defaultMessage="Free for you!"
                                            id="order_summary.label.free.garage"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Free for you!"
                                            id="order_summary.label.free.dynamite"
                                        />
                                    )}
                                </Text>
                            ) : (
                                <Text
                                    data-testid="order-summary-shipping"
                                    {...orderSummaryTheme.rowText}
                                >
                                    {/* BD REQUIREMENT: shippingTotal must be the shipping cost AFTER deducting any shipping promotions.
                                        FD assumes this value is already net of free shipping and other shipping promos. */}
                                    {isCheckout && Number(basket.shippingTotal) === 0 ? (
                                        <FormattedMessage
                                            defaultMessage="FREE"
                                            id="order_summary.label.free_shipping"
                                        />
                                    ) : (
                                        <FormattedNumber
                                            value={basket.shippingTotal}
                                            style="currency"
                                            currency={basket.currency}
                                        />
                                    )}
                                </Text>
                            )}
                        </Flex>

                        <Flex {...orderSummaryTheme.row} aria-live="polite" aria-atomic="true">
                            <Flex {...orderSummaryTheme.shippingLabelWrap}>
                                <Text {...orderSummaryTheme.shippingLabel}>
                                    {isEstimate ? (
                                        <FormattedMessage
                                            defaultMessage="Estimated taxes"
                                            id="order_summary.label.estimated_taxes"
                                        />
                                    ) : (
                                        <FormattedMessage
                                            defaultMessage="Taxes"
                                            id="order_summary.label.taxes"
                                        />
                                    )}
                                </Text>

                                {isEstimate && (
                                    <Tooltip
                                        hasArrow={false}
                                        placement="top"
                                        variant="info"
                                        label={
                                            <FormattedMessage
                                                defaultMessage="Taxes are estimated and may change based on your postal code and will be calculated at checkout."
                                                id="order_summary.tooltip.estimated_taxes"
                                            />
                                        }
                                    >
                                        <IconButton
                                            variant="unstyled"
                                            display={'flex'}
                                            minW={'auto'}
                                            h={'20px'}
                                            width={'20px'}
                                            aria-label="Aria label button"
                                            data-testid="order-summary-button-estimated-taxes-tooltip"
                                            icon={<InfoCircle width={'20px'} height={'20px'} />}
                                        />
                                    </Tooltip>
                                )}
                            </Flex>

                            <Text data-testid="order-summary-tax" {...orderSummaryTheme.rowText}>
                                {/* TODO BD: taxTotal must be calculated by BD using Vertex API + IP geolocation */}
                                {/* while isEstimate is true (no real shipping address yet), a $0 taxTotal is not a real value, so show TBD */}
                                {basket?.taxTotal != null &&
                                !(isEstimate && Number(basket.taxTotal) === 0) ? (
                                    <FormattedNumber
                                        style="currency"
                                        currency={basket?.currency}
                                        value={basket?.taxTotal}
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="TBD"
                                        id="order_summary.label.tbd"
                                    />
                                )}
                            </Text>
                        </Flex>

                        <Flex {...orderSummaryTheme.totalRow} aria-live="polite" aria-atomic="true">
                            <Text {...orderSummaryTheme.totalText}>
                                {isEstimate ? (
                                    <FormattedMessage
                                        defaultMessage="Estimated Total"
                                        id="order_summary.label.estimated_total"
                                    />
                                ) : (
                                    <FormattedMessage
                                        defaultMessage="Total"
                                        id="order_summary.label.total"
                                    />
                                )}
                            </Text>
                            <Text
                                data-testid="order-summary-total"
                                {...orderSummaryTheme.totalText}
                            >
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={estimatedTotalValue - giftCardTotal}
                                />
                            </Text>
                        </Flex>
                    </Stack>

                    {footerContent}

                    {showCartItems && (
                        <CartItemsAccordion
                            basket={basket}
                            totalItems={totalItems}
                            isCheckout={isCheckout}
                            onItemsAutoRemoved={otherProps.onItemsAutoRemoved}
                        />
                    )}
                </Stack>
            </Stack>
        </>
    )
}

const OrderSummary = ({
    basket,
    totalItems,
    showCartItems = false,
    isEstimate = false,
    isCheckout = false,
    isOrderConfirmation = false,
    savings,
    showHeading = true,
    showPoints = true,
    isMobileAccordion = false,
    variant,
    footerContent,
    ...otherProps
}) => {
    const isDesktop = useBreakpointValue({base: false, lg: true})
    const {isGarage} = useBrand()
    const orderSummaryTheme = useMultiStyleConfig('OrderSummary', {variant: variant})
    const [productsDiscountTotal, setProductsDiscountTotal] = useState(0)
    const {isOpen: isOrderDrawerOpen, onOpen: onOrderDrawerOpen, onClose: onOrderDrawerClose} = useDisclosure()
    const {formatMessage} = useIntl()
    const {checkoutError, setCheckoutError, setIsOrderSummaryDrawerOpen} = useCheckoutProgress()
    const {buildUrl} = useMultiSite()
    const checkoutErrorEntries = checkoutError
        ? (Array.isArray(checkoutError) ? checkoutError : [checkoutError])
        : []

    useEffect(() => {
        if (!isCheckout) return
        setIsOrderSummaryDrawerOpen(isOrderDrawerOpen)
        return () => setIsOrderSummaryDrawerOpen(false)
    }, [isCheckout, isOrderDrawerOpen, setIsOrderSummaryDrawerOpen])
    const shouldHideLoyaltyUI = useHideLoyaltyUIForInternational()

    const shippingItem = basket?.shippingItems?.[0]
    // c_loyaltyV2PointsEarned is already the final value (double points applied server-side); don't re-multiply.
    const pointsEarned = Number(basket?.c_loyaltyV2PointsEarned) || 0

    useEffect(() => {
        if (basket?.c_productsDiscountTotal !== undefined) {
            setProductsDiscountTotal(basket.c_productsDiscountTotal)
        }
    }, [basket?.c_productsDiscountTotal])

    if (!basket?.basketId && !basket?.orderNo) {
        return null
    }

    return (
        <Stack
            data-testid="sf-order-summary"
            {...orderSummaryTheme.container}
            {...(isMobileAccordion ? orderSummaryTheme.mobileContainer : {})}
        >
            {isMobileAccordion && !isDesktop && isCheckout && (
                <>
                    <Flex {...orderSummaryTheme.mobileTopBar}>
                        <Flex {...orderSummaryTheme.mobileTopBarTotalWrap}>
                            <Text {...orderSummaryTheme.mobileTopBarLabel}>
                                <FormattedMessage
                                    defaultMessage="Estimated Total"
                                    id="order_summary.label.estimated_total_topbar"
                                />
                            </Text>
                            <Text {...orderSummaryTheme.mobileTopBarTotal}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={basket?.orderTotal || basket?.productTotal}
                                />
                            </Text>
                        </Flex>
                        <Text {...orderSummaryTheme.mobileViewOrderButton} onClick={onOrderDrawerOpen}>
                            <FormattedMessage
                                defaultMessage="View Order ({totalItems})"
                                id="order_summary.button.view_order"
                                values={{totalItems}}
                            />
                        </Text>
                    </Flex>
                    <WebDrawer
                        isOpen={isOrderDrawerOpen}
                        onClose={onOrderDrawerClose}
                        title={
                            isGarage ? (
                                <FormattedMessage
                                    defaultMessage="Your Order ({totalItems})"
                                    id="order_summary.drawer.your_order.garage"
                                    values={{totalItems}}
                                />
                            ) : (
                                <FormattedMessage
                                    defaultMessage="Your Order ({totalItems})"
                                    id="order_summary.drawer.your_order.dynamite"
                                    values={{totalItems}}
                                />
                            )
                        }
                        size="85vh"
                    >
                        {checkoutErrorEntries.length > 0 && (
                            <Stack spacing={4} mb={4}>
                                {checkoutErrorEntries.map((entry, i) => {
                                    const message = typeof entry === 'string' ? entry : entry?.message
                                    const entryLabel = typeof entry === 'object' ? entry?.label : null
                                    const entryKey = typeof entry === 'object' ? entry?.key || i : message
                                    return (
                                        <GlobalErrorCallout
                                            key={entryKey}
                                            label={
                                                entryLabel ||
                                                formatMessage({
                                                    id: 'checkout.error.label',
                                                    defaultMessage: 'Error'
                                                })
                                            }
                                            message={message}
                                            onClose={() => setCheckoutError(null)}
                                        />
                                    )
                                })}
                            </Stack>
                        )}
                        <Text
                            as={Link}
                            to="/cart"
                            {...orderSummaryTheme.editCartLink}
                            data-testid="order-summary-link-edit-cart"
                        >
                            <FormattedMessage
                                defaultMessage="Edit Cart"
                                id="order_summary.label.edit_cart"
                            />
                        </Text>
                        <Stack {...orderSummaryTheme.checkoutProductListStack}>
                            <ProductItemList
                                isMiniCart={true}
                                hideRemoveAction={true}
                                hideWishlistAction={true}
                                variant="checkout-order-summary"
                            />
                        </Stack>
                    </WebDrawer>
                </>
            )}
            {isMobileAccordion && !isDesktop && !isCheckout && (
                <FocusedAccordion
                    AccordionButtonContent={
                        <Flex {...orderSummaryTheme.accordionHeader}>
                            <Text {...orderSummaryTheme.accordionTitle}>
                                Your total ({totalItems})
                            </Text>
                            <Text {...orderSummaryTheme.accordionAmount}>
                                <FormattedNumber
                                    style="currency"
                                    currency={basket?.currency}
                                    value={basket?.orderTotal || basket?.productTotal}
                                />
                            </Text>
                            <AccordionIcon />
                        </Flex>
                    }
                    AccordionPanelContent={
                        <Stack {...orderSummaryTheme.accordionPanel}>
                            {renderOrderSummaryContent({
                                showHeading,
                                isCheckout,
                                isOrderConfirmation,
                                basket,
                                orderSummaryTheme,
                                savings,
                                shippingItem,
                                showPoints,
                                showCartItems,
                                totalItems,
                                isEstimate,
                                productsDiscountTotal,
                                footerContent,
                                shouldHideLoyaltyUI,
                                buildUrl,
                                pointsEarned,
                                ...otherProps
                            })}
                        </Stack>
                    }
                />
            )}
            {(!isCheckout || !isMobileAccordion) &&
                renderOrderSummaryContent({
                    showHeading,
                    isCheckout,
                    isOrderConfirmation,
                    basket,
                    orderSummaryTheme,
                    savings,
                    shippingItem,
                    showPoints,
                    showCartItems,
                    totalItems,
                    isEstimate,
                    productsDiscountTotal,
                    footerContent,
                    shouldHideLoyaltyUI,
                    buildUrl,
                    pointsEarned,
                    ...otherProps
                })}
        </Stack>
    )
}

OrderSummary.propTypes = {
    basket: PropTypes.object,
    totalItems: PropTypes.number,
    showCartItems: PropTypes.bool,
    isEstimate: PropTypes.bool,
    savings: PropTypes.number,
    isCheckout: PropTypes.bool,
    isOrderConfirmation: PropTypes.bool,
    showPoints: PropTypes.bool,
    showHeading: PropTypes.bool,
    isMobileAccordion: PropTypes.bool,
    footerContent: PropTypes.node
}

export default OrderSummary
